import { useEffect, useState, useCallback, useLayoutEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  GridColDef, 
  GridActionsCellItem,
  GridRowParams,
  GridToolbarContainer,
  GridToolbarColumnsButton,
  GridToolbarFilterButton,
  GridToolbarExport,
  GridToolbarDensitySelector,
  GridRowSelectionModel,
  GridInitialState,
  useGridApiRef,
  GridColumnVisibilityModel,
  GRID_CHECKBOX_SELECTION_FIELD,
} from '@mui/x-data-grid-pro';
import { ResponsiveDataGrid } from 'ui-component/ResponsiveDataGrid';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import EditIcon from '@mui/icons-material/Edit';
import ArchiveOutlinedIcon from '@mui/icons-material/ArchiveOutlined';
import UnarchiveOutlinedIcon from '@mui/icons-material/UnarchiveOutlined';
import {
  Box,
  Button,
  Chip,
  Tooltip,
  CircularProgress,
  Stack,
  Typography,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  InputLabel,
  Avatar,
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import ConfirmationDialog from 'ui-component/ConfirmationDialog';
import ErrorDialog from 'ui-component/ErrorDialog';
import TagsEditor from 'ui-component/TagsEditor';
import { useTheme } from '@mui/material/styles';
import { IUser} from './../../../../types/user.interface';
import { ITag } from 'types/tag';
import { useUsers } from '../../../../hooks/useUsers.hooks';
import { useCurrentOrganization } from './../../../../hooks/useOrganization.hooks'
import { useUser } from '../../../../hooks/useUser.hooks';
import { useDispatch } from 'store';
import { openSnackbar } from 'store/slices/snackbar';
import TableButton from 'ui-component/TableButton';

interface EditToolbarProps {
  setError: any;
  setShowErrorDialog: any;
  rowSelectionModel: GridRowSelectionModel;
  setShowSetProfileTagsDialog: any;
}

function EditToolbar(props: EditToolbarProps) {
  const { setError, setShowErrorDialog, rowSelectionModel, setShowSetProfileTagsDialog } = props;
  
  const handleClickSetProfileTags = () => {
    
    if (rowSelectionModel.length == 0)
    {
      setError("Please select at least one delivery driver to set the profile tags on");
      setShowErrorDialog(true);
      return;
    }
    
    setShowSetProfileTagsDialog(true);
  };
  
  return (
    <GridToolbarContainer>
      <Button color="primary" startIcon={<LocalOfferIcon />} onClick={handleClickSetProfileTags}>
        Set Profile Tags of Selected Drivers
      </Button>
      <GridToolbarColumnsButton />
      <GridToolbarFilterButton />
      <GridToolbarDensitySelector />
      <GridToolbarExport />
    </GridToolbarContainer>
  );
}

export default function UsersDataGrid(props: any) {
  const {setUser, role, active} = props;
  const theme = useTheme();
  const usersState = useUsers(role, active);
  const userState = useUser();
  const organizationState = useCurrentOrganization();
  const navigate = useNavigate();
  const [showErrorDialog, setShowErrorDialog] = useState(false);
  const [error, setError] = useState<any>(null);
  const [showArchiveConfirmation, setShowArchiveConfirmation] = useState(false);
  const [showReactivateConfirmation, setShowReactivateConfirmation] = useState(false);
  const [activeUserID, setActiveUserID] = useState(-1);
  const [rowSelectionModel, setRowSelectionModel] = useState<GridRowSelectionModel>([]);
  const [showSetProfileTagsDialog, setShowSetProfileTagsDialog] = useState(false);
  const [tags, setTags] = useState([]);
  const [resendInProgressId, setResendInProgressId] = useState<number | null>(null);

  const apiRef = useGridApiRef();
  const dispatch = useDispatch();
  const [initialState, setInitialState] = useState<GridInitialState>();

  const saveSnapshot = useCallback(() => {
    if (apiRef?.current?.exportState && localStorage) {
      const currentState = apiRef.current.exportState();
      localStorage.setItem('adminDeliveryDriversUserDataGridState', JSON.stringify(currentState));
    }
  }, [apiRef]);

  useLayoutEffect(() => {
    const stateFromLocalStorage = localStorage?.getItem('adminDeliveryDriversUserDataGridState');
    setInitialState(stateFromLocalStorage ? JSON.parse(stateFromLocalStorage) : {});

    // handle refresh and navigating away/refreshing
    window.addEventListener('beforeunload', saveSnapshot);

    return () => {
      // in case of an SPA remove the event-listener
      window.removeEventListener('beforeunload', saveSnapshot);
      saveSnapshot();
    };
  }, [saveSnapshot]);

  const [columnVisibilityModel, setColumnVisibilityModel] =
    useState<GridColumnVisibilityModel>({
      // Show 'neighbourhood' column if the admin is part of Fresh Routes
      neighbourhood: (organizationState.ready && organizationState?.organization.id == 3) ? true : false,
    });

  const formatDateTime = (value?: string | null) => {
    if (!value) {
      return 'Never';
    }
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? 'Never' : date.toLocaleString();
  };

  const handleResendSetupEmail = async (targetUser: IUser) => {
    if (!userState.resendSetupEmail) {
      return;
    }

    setResendInProgressId(targetUser.id);
    try {
      const data = await userState.resendSetupEmail(targetUser.email);
      dispatch(
        openSnackbar({
          open: true,
          message: data?.message || 'Verification email resent successfully.',
          variant: 'alert',
          alert: { color: 'success' },
          close: false,
        }),
      );
    } catch (error: any) {
      const message = error?.response?.data?.error || error?.response?.data?.message || error?.message || 'Unable to send verification email.';
      dispatch(
        openSnackbar({
          open: true,
          message,
          variant: 'alert',
          alert: { color: 'error' },
          close: false,
        }),
      );
    } finally {
      setResendInProgressId(null);
    }
  };

  // Calculate online status from last_activity timestamp
  const getOnlineStatus = (lastActivity: string | null) => {
    if (!lastActivity) {
      return { status: 'never', text: 'N/A', color: '#9e9e9e' }; // Grey
    }

    const now = new Date().getTime();
    const lastActivityTime = new Date(lastActivity).getTime();
    const minutesAgo = Math.floor((now - lastActivityTime) / (1000 * 60));

    if (minutesAgo < 5) {
      return { status: 'online', text: 'Online', color: '#4caf50' }; // Green
    } else if (minutesAgo < 60) {
      return { status: 'recent', text: `${minutesAgo}m ago`, color: '#ff9800' }; // Orange/Yellow
    } else {
      const hoursAgo = Math.floor(minutesAgo / 60);
      const daysAgo = Math.floor(hoursAgo / 24);
      
      if (daysAgo > 0) {
        return { status: 'offline', text: `${daysAgo}d ago`, color: '#f44336' }; // Red
      } else {
        return { status: 'offline', text: `${hoursAgo}h ago`, color: '#f44336' }; // Red
      }
    }
  };

  const columns: GridColDef[] = [
    ...(!active ? [{
      field: 'time_remaining', 
      headerName: 'Remaining time until permanently deleted', 
      width: 150,
      sortable: true,
      type: 'number' as const,
      renderCell: (params: { row: any; }) => (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100%',
          width: '100%',
        }}
      >
        <Chip 
        style={{ backgroundColor: theme.palette.error.light, alignItems: 'center' }}
        label={params.row.time_remaining + " days"}
      />
      </Box>
      )
    }] : []),
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      width: 100,
      cellClassName: 'actions',
      getActions: (params: GridRowParams) => {
        if (active) {
          return [
            <GridActionsCellItem
              icon={
                <Tooltip title="Edit">
                  <EditIcon />
                </Tooltip>
              }
              label="Edit"
              className="textPrimary"
              onClick={() => navigate('/delivery-driver/update/' + params.row.id)}
              color="inherit"
            />,
            <GridActionsCellItem
              icon={
                <Tooltip title="Archive">
                  <ArchiveOutlinedIcon />
                </Tooltip>
              }
              label="Archive"
              onClick={() => {
                setActiveUserID(params.row.id as number);
                setShowArchiveConfirmation(true);
              }}
              color="inherit"
            />,
          ];
        }
        return [
          <GridActionsCellItem
            icon={
              <Tooltip title="Reactivate">
                <UnarchiveOutlinedIcon />
              </Tooltip>
            }
            label="Reactivate"
            onClick={() => {
              setActiveUserID(params.row.id as number);
              setShowReactivateConfirmation(true);
            }}
            color="inherit"
          />,
        ];
      },
    },
    { 
      field: 'online_status', 
      headerName: 'Online Status', 
      width: 120,
      sortable: true,
      valueGetter: (value, row) => {
        const statusInfo = getOnlineStatus(row.last_activity);
        return statusInfo.text;
      },
      renderCell: (params: { row: any; }) => {
        const statusInfo = getOnlineStatus(params.row.last_activity);
        return (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              height: '100%',
            }}
          >
            <Box
              sx={{
                width: 10,
                height: 10,
                borderRadius: '50%',
                backgroundColor: statusInfo.color,
              }}
            />
            {statusInfo.text}
          </Box>
        );
      },
    },
    { field: 'region_name', headerName: 'Region', width: 100,
    valueGetter: (value, row) =>
      {
        return `${row.region.name}`},
    },
    {
      field: 'avatar',
      headerName: 'Photo',
      width: 70,
      sortable: false,
      filterable: false,
      disableExport: true,
      renderCell: (params) => {
        const { first_name, last_name, image_url } = params.row;
        const initials = `${first_name?.[0] || ''}${last_name?.[0] || ''}`;

        return (
          <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
            <Avatar
              src={image_url ?? undefined}
              alt={`${first_name} ${last_name}`}
              sx={{width: 40, height: 40, bgcolor: 'grey.100', color: 'grey.500'}}
            >
              {!image_url && (initials || <PersonIcon fontSize="small" />)}
            </Avatar>
          </Box>
        );
      },
    },
    { field: 'first_name', headerName: 'First Name', width: 130 },
    { field: 'last_name', headerName: 'Last name', width: 130 },
    { field: 'email', headerName: 'Email', width: 300 },
    { field: 'phone', headerName: 'Phone Number', width: 200 },
    { field: 'profile_tags',
      headerName: 'Profile Tags',
      width: 300,
      valueGetter: (value, row) => { // Set tag names as string for filters
        const tags: ITag[] = row.profile_tags || [];
        return tags.map((tag) => tag.name).join(', ');
      },
      renderCell: (params) => {
        const tags: ITag[] = params.row.profile_tags || [];
        return (
          <Tooltip title={tags.map((tag: ITag) => tag.name).join(", ")} >
          <Box component="div" sx={{ display: 'flex', alignItems: 'center', height: '100%', width: '100%', gap: 0.5 }} >
            {tags.map((tag: ITag)=>(
              <Chip
                key={tag.id}
                style={{ backgroundColor: tag.color }}
                label={tag.name}
              />
            ))}
          </Box>
          </Tooltip>
        )
      },
    },
    { field: 'neighbourhood', headerName: 'Neighbourhood', width: 130 },
    { field: 'password_set', headerName: 'Password Set', width: 140,
      valueGetter: (value, row) => (row?.must_setup_password ? 'No' : 'Yes')
    },
    { field: 'email_verified', headerName: 'Email Verified', width: 140,
      valueGetter: (value, row) => ((row?.email_verified || row?.email_verified_at) ? 'Yes' : 'No')
    },
    { field: 'last_setup_email_sent', headerName: 'Last Setup Email', width: 190,
      valueGetter: (value, row) => formatDateTime(row?.last_setup_email_sent)
    },
    {
      field: 'resend_setup_email',
      headerName: 'Resend Verification',
      width: 200,
      sortable: false,
      filterable: false,
      renderCell: (params: { row: IUser }) => {
        const handleClick = (event: any) => {
          event.stopPropagation();
          handleResendSetupEmail(params.row);
        };

        const isProcessing = resendInProgressId === params.row.id;
        return (
          <TableButton
            onClick={handleClick}
            disabled={!userState.resendSetupEmail || isProcessing}
          >
            {isProcessing ? 'Sending...' : 'Resend Verification'}
          </TableButton>
        );
      },
    },
    {
      field: 'id',
      headerName: 'ID',
      type: 'string',
      width: 100,
      editable: false,
    },
  ];

  const [rows, setRows] = useState<IUser[]>([]);
  let usersArray : IUser[]
  useEffect(() => {
    if (usersState?.ready && organizationState.ready && !userState.deleteInProgress) {
      usersArray = Object.values(usersState.users).sort((a : any, b : any) => a.first_name.localeCompare(b.first_name));
      usersArray = usersArray.map((user : any)=>{
        user.profile_tags = user.tags?.map((tag:ITag) => ({ id: tag.id, name: tag.name, color: tag.color })) || [];
        return user;
    })
      setRows(usersArray);
    }
  }, [usersState?.ready, organizationState.ready, userState?.deleteInProgress, usersState.users]);
  
  const onRowsSelectionHandler = (ids: any) => {
    setRowSelectionModel(ids);
  };

  const cancelDialog = () => {
    setShowArchiveConfirmation(false);
    setShowReactivateConfirmation(false);
    setShowSetProfileTagsDialog(false);
  }
  
  const setProfileTags = async () => {
    await usersState.setProfileTags({
      userIDs: rowSelectionModel,
      tagIDs: tags
    }); 
    dispatch(
        openSnackbar({
            open: true,
            message: 'Profile tags updated.',
            variant: 'alert',
            alert: {
                color: 'success'
            },
            close: false
        })
    );
    setShowSetProfileTagsDialog(false);
  };

  const confirmArchive = async () => {
    await userState.deleteUser(activeUserID);
    await usersState.fetch(); // to refresh users
    setShowArchiveConfirmation(false);
  };

  const confirmReactivate = async () => {
    await userState.reactivateUser(activeUserID);
    await usersState.fetch(); // to refresh users
    setShowReactivateConfirmation(false);
  };

  if (!usersState?.ready) {
    return (
      <div>Loading...</div>
  )
  }

  // Handle saved table views
  if (!initialState) {
    return <CircularProgress />;
  }

  return (
    <div style={{ height: 700, width: '100%' }}>
      <Box
        sx={{
        height: 700,
        width: '100%',
        '& .actions': {
          color: 'text.secondary',
        },
        '& .textPrimary': {
          color: 'text.primary',
        },
        '& .MuiDataGrid-root': {
            border: 'none',
            '& .MuiDataGrid-cell': {
                borderColor: theme.palette.mode === 'dark' ? theme.palette.text.primary + 15 : 'grey.200'
            },
            '& .MuiDataGrid-columnsContainer': {
                color: theme.palette.mode === 'dark' ? 'grey.600' : 'grey.900',
                borderColor: theme.palette.mode === 'dark' ? theme.palette.text.primary + 15 : 'grey.200'
            },
            '& .MuiDataGrid-columnSeparator': {
                color: theme.palette.mode === 'dark' ? theme.palette.text.primary + 15 : 'grey.200'
            }
          },
          '& .MuiDataGrid-columnHeader, .MuiDataGrid-cell': {
            borderRight: `1px solid ${
              theme.palette.mode === 'light' ? '#f0f0f0' : '#303030'
            }`,
          },
      }}
      >
      <ResponsiveDataGrid
      checkboxSelection={true}
      rows={rows} 
      columns={columns}
      pagination
      slots={{ toolbar: EditToolbar as any }}
      slotProps={{
          toolbar: { setError, setShowErrorDialog, setShowSetProfileTagsDialog, rowSelectionModel } as any,
      }}
      onRowSelectionModelChange={(ids : any) => onRowsSelectionHandler(ids)}
      onCellClick={(params) => {
        if (params.field !== GRID_CHECKBOX_SELECTION_FIELD) {
          setUser(params.row); // Open and scroll to the user's route instance table
        }
      }}
      apiRef={apiRef}
      initialState={{
        ...initialState,
        sorting: {
          sortModel: [{ field: 'time_remaining', sort: 'asc' }],
        },
      }}
      columnVisibilityModel={columnVisibilityModel}
      onColumnVisibilityModelChange={(newModel) =>
        setColumnVisibilityModel(newModel)
      }
      />
      
      {/* Dialog for setting profile tags on multiple delivery drivers */}
      <Dialog
        onClose={cancelDialog}
        open={showSetProfileTagsDialog}
        maxWidth="xl"
      >
          <DialogTitle>Set Profile Tags</DialogTitle>
          <DialogContent dividers>
              <Stack spacing={3}>
                <DialogContentText>
                    <Typography variant="body2" component="div">
                        Assign profile tags for the selected delivery drivers. Note that this will overwrite existing profile tags
                        previously set for any of the selected drivers.
                    </Typography>
                </DialogContentText>
                <InputLabel>Tags</InputLabel>
                <TagsEditor
                    setChecked={(newValue: any) => (setTags(newValue.map(function(value:any) { return value.id })))}
                    initialValues={tags}
                />
              </Stack>
            </DialogContent>
            <DialogActions sx={{ pr: 2.5 }}>
                <Button 
                  onClick={cancelDialog}
                >
                  Cancel
                </Button>
                <Button 
                  onClick={()=>setProfileTags()} >
                    Save
                </Button>
            </DialogActions>
      </Dialog>
      
      {/* General Errors */}
      <ErrorDialog
          open={showErrorDialog}
          onClose={() => {
              setShowErrorDialog(false);
              setError(null);
          }}
          error={error}
          title="Error"
      />
      <ConfirmationDialog
          open={showArchiveConfirmation}
          onCancel={cancelDialog}
          onConfirm={confirmArchive}
          title="Archive User?"
          message="Confirm you want to archive this user?"
      />
      <ConfirmationDialog
          open={showReactivateConfirmation}
          onCancel={cancelDialog}
          onConfirm={confirmReactivate}
          title="Reactivate User?"
          message="Confirm you want to reactivate this user?"
      />
      </Box>
    </div>
  );
}
